import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { landAxios } from '@Root/utils/landAxios'
import MyhouseNotifyResponse from '@Types/entities/MyhouseNotifyResponse'
import MyhouseMainHouseResponse from '@Types/entities/MyhouseMainHouseResponse'
import { FETCH_STATUS } from '@Root/contants'
import MyhouseNotifyEtiqParam from '@Types/entities/MyhouseNotifyEtiqParam'
import { makeHouseLabel, MyhouseLabelType } from '@Root/utils/houseLabelUtils'
import FetchResponse from '@Types/inner/FetchResponse'

export const fetchNotifyList = createAsyncThunk('/notify/list', async () => {
  return await (await landAxios.get(`/myhouse/v2`)).data
})

export const fetchNotifyGet = createAsyncThunk('/notify/get', async (houseNo: number) => {
  return await (await landAxios.get(`/myhouse/notify/${houseNo}`)).data
})

export const fetchNotifyEtiquette = createAsyncThunk('/notify/etiquette/patch', async (param: MyhouseNotifyEtiqParam) => {
  return await (await landAxios.patch(`/myhouse/notify/etiquette`, param)).data
})

export const fetchNotify = createAsyncThunk('/notify/patch', async (param: { houseNo: number; notifyYn: boolean; [p: string]: number | boolean }) => {
  return await (await landAxios.patch(`/myhouse/notify`, param)).data
})

export type MyhouseNotifyDetail = {
  routeHouseNo: number
  currentHouseNo: number
  buildHo: string
  isTradeOwner: boolean // 거래타입이 매매 인가?
  isTradeEndYmd: boolean // 거래타입이 전월세인 경우 거래 종료일이 있는가?
  isInsuranceYmd: boolean // 보험만기일이 있는가?
  isNaverDB: boolean // complexNo 존재하면 naver db 매물
  isArticleNotifyYn: boolean // !complexNo && D02,부동산분류코드-상가, && D03, 부동산분류코드-건물 then 신규매물 안보임.
  isNotifyExists: boolean
  fetchStatus: FETCH_STATUS
}

export type MyhouseNotifyResponseType = {
  notifyList: MyhouseMainHouseResponse[]
  tabList: MyhouseLabelType[]
  notify: FetchResponse<MyhouseNotifyResponse>
  detail: MyhouseNotifyDetail
}

const initialState: MyhouseNotifyResponseType = {
  notifyList: [],
  tabList: [],
  notify: { fetchStatus: FETCH_STATUS.INITIAL } as FetchResponse<MyhouseNotifyResponse>,
  detail: { fetchStatus: FETCH_STATUS.INITIAL } as MyhouseNotifyDetail,
} as MyhouseNotifyResponseType

export const notifySlice = createSlice({
  name: 'notify',
  initialState,
  reducers: {
    updateCurrentHouseNo: (state: MyhouseNotifyResponseType, action: PayloadAction<{ currentHouseNo: number }>) => {
      state.detail.currentHouseNo = action.payload.currentHouseNo
    },
    updateToggle: (state: MyhouseNotifyResponseType, action: PayloadAction<{ key: string; value: boolean }>) => {
      state.notify = Object.assign({}, state.notify, {
        [action.payload.key]: action.payload.value,
      })
    },
    updateRouteHouseNo: (state: MyhouseNotifyResponseType, action: PayloadAction<number>) => {
      state.detail.routeHouseNo = action.payload
    },
  },
  extraReducers: {
    [fetchNotifyList.fulfilled.type]: (state: MyhouseNotifyResponseType, action: PayloadAction<MyhouseMainHouseResponse[]>) => {
      state.notifyList = action.payload
      const houseList: MyhouseMainHouseResponse[] = action.payload
      state.tabList = makeHouseLabel(houseList)
      if (houseList?.length > 0) {
        state.detail.currentHouseNo = !state.detail.routeHouseNo ? action.payload[0].houseNo : state.detail.routeHouseNo
      } else {
        state.detail.currentHouseNo = -1
      }
    },
    [fetchNotifyGet.fulfilled.type]: (state: MyhouseNotifyResponseType, action: PayloadAction<MyhouseNotifyResponse>) => {
      state.notify = { ...action.payload, fetchStatus: FETCH_STATUS.SUCCESS }
      state.detail.isTradeOwner = action.payload.tradeTypeCode === 'A1' // 거래 타입이 매매인가?
      state.detail.isTradeEndYmd = action.payload.tradeTypeCode !== 'A1' && !!action.payload.tradeEndYmd // 거래 타입이 매매가 아닌고, 거래일이 존재하는가?
      state.detail.isInsuranceYmd = !!action.payload.insuranceYmd // 보험만료일이 존재하는가?
      state.detail.isNaverDB = !!action.payload.complexNo
      state.detail.isArticleNotifyYn = !['SG', 'GM'].includes(action.payload.realestateCategoryCode)
      state.detail.isNotifyExists = !state.detail.isTradeOwner || state.detail.isInsuranceYmd || state.detail.isNaverDB || state.detail.isArticleNotifyYn
    },
    [fetchNotifyGet.pending.type]: (state: MyhouseNotifyResponseType) => {
      state.notify.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchNotifyGet.rejected.type]: (state: MyhouseNotifyResponseType) => {
      state.notify.fetchStatus = FETCH_STATUS.FAILURE
    },
    [fetchNotify.fulfilled.type]: (state: MyhouseNotifyResponseType) => {
      state.detail.fetchStatus = FETCH_STATUS.SUCCESS
    },
    [fetchNotify.pending.type]: (state: MyhouseNotifyResponseType) => {
      state.detail.fetchStatus = FETCH_STATUS.PENDING
    },
    [fetchNotify.rejected.type]: (state: MyhouseNotifyResponseType) => {
      state.detail.fetchStatus = FETCH_STATUS.FAILURE
    },
  },
})

export const { updateCurrentHouseNo, updateToggle, updateRouteHouseNo } = notifySlice.actions
export default notifySlice.reducer
